import { ProviderType } from 'features/connections'
import { MapDataset } from 'features/maps/types'
import { formatSource, formatPGRSSource, getQFromQuery, buildAnalysisQueryFromSource } from '../../utils'
import { FilterNumber, FilterText, FilterTypes } from '../types'
import { generateQuery as generateQueryForBigQuery } from './bq'
import { generateQuery as generateQueryForRedshift } from './rs'
import { generateQuery as generateQueryForSnowflake } from './sf'
import { generateQuery as generateQueryForPostgres } from './pg'

export interface FilterByColumnQuery {
  sourceDataset?: MapDataset & { fields; metadata }
  sourceColumn?: string
  filterType?: FilterTypes
  filterNumberType?: FilterNumber
  filterTextType?: FilterText
  filterValue?: string[] | number[]
  filterNumberFirst?: number
  filterNumberSecond?: number
  withAs?: string
  from?: string
}

export const getFormattedSource = ({ sourceDataset }) => {
  let from
  const connectionProvider = sourceDataset.providerId || sourceDataset.provider
  if (connectionProvider === ProviderType.REDSHIFT || connectionProvider === ProviderType.POSTGRES) {
    from = `${formatPGRSSource(sourceDataset.source)}`
  } else if (connectionProvider === ProviderType.BIGQUERY) {
    from = `\`${sourceDataset.source}\``
  } else {
    from = `${formatSource(sourceDataset.source)}`
  }
  return from
}

export function numberFilterCondition({
  provider,
  sourceColumn,
  filterNumberType,
  filterNumberFirst,
  filterNumberSecond
}) {
  const column = provider === ProviderType.POSTGRES ? `"${sourceColumn}"` : `${sourceColumn}`
  switch (filterNumberType) {
    case FilterNumber.Equal:
      return `${column} = ${filterNumberFirst}`
    case FilterNumber.Between:
      return `${column} >= ${filterNumberFirst} AND ${sourceColumn} <= ${filterNumberSecond}`
    case FilterNumber.GreaterThan:
      return `${column} > ${filterNumberFirst}`
    case FilterNumber.LessThan:
      return `${column} < ${filterNumberFirst}`
    case FilterNumber.GreaterThanOrEqual:
      return `${column} >= ${filterNumberFirst}`
    case FilterNumber.LessThanOrEqual:
      return `${column} <= ${filterNumberFirst}`
    default:
      throw new Error('Unknown filter type')
  }
}

export function generateFilterByColumnQuery(payload: FilterByColumnQuery) {
  const { sourceDataset } = payload
  if (!sourceDataset) return
  const connectionProvider = sourceDataset?.providerId
  const baseQuery = buildAnalysisQueryFromSource({ source: sourceDataset })
  const baseQNumber = getQFromQuery(baseQuery)
  payload.withAs = baseQuery
  payload.from = `__q${baseQNumber}`

  switch (connectionProvider) {
    case ProviderType.BIGQUERY:
      return generateQueryForBigQuery(payload)
    case ProviderType.REDSHIFT:
      return generateQueryForRedshift(payload)
    case ProviderType.SNOWFLAKE:
      return generateQueryForSnowflake(payload)
    case ProviderType.POSTGRES:
      return generateQueryForPostgres(payload)
    default:
      throw new Error('Unknown provider to build the query')
  }
}
